import { useState, useRef, useCallback } from 'react'
import { apiFetch } from '../lib/apiFetch'
import type { Room } from 'livekit-client'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-stt')

export interface SubtitleLine {
  id: string
  text: string
  isFinal: boolean
  lang: string
}

export const STT_LANGS = ['', 'en', 'fr', 'de', 'it', 'pl', 'es', 'pt', 'nl'] as const
export type SttLang = typeof STT_LANGS[number]

export function useSubtitles(room: Room | undefined, roomId?: string) {
  const [lines, setLines] = useState<SubtitleLine[]>([])
  const [partial, setPartial] = useState<SubtitleLine | null>(null)
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [lang, setLang] = useState<SttLang>('')
  const wsRef = useRef<WebSocket | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)
  const sourcesRef = useRef<MediaStreamAudioSourceNode[]>([])
  const trackListenerRef = useRef<(((...args: any[]) => void) | null)>(null)
  const pendingTranscripts = useRef<{ text: string; speaker: string; lang: string }[]>([])
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const flushTranscripts = useCallback(async () => {
    if (!roomId || pendingTranscripts.current.length === 0) return
    const batch = pendingTranscripts.current.splice(0)
    try {
      await apiFetch(`/api/visio/rooms/${roomId}/transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: batch }),
      })
    } catch {
      // Put back on failure
      pendingTranscripts.current.unshift(...batch)
    }
  }, [roomId])

  const cleanup = useCallback(() => {
    // Flush any remaining transcripts before cleanup
    flushTranscripts()
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current)
      flushTimerRef.current = null
    }
    sourcesRef.current.forEach(s => { try { s.disconnect() } catch {} })
    sourcesRef.current = []
    if (workletRef.current) {
      try { workletRef.current.disconnect() } catch {}
      workletRef.current = null
    }
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      ctxRef.current.close().catch(() => {})
      ctxRef.current = null
    }
    if (room && trackListenerRef.current) {
      room.off('trackSubscribed' as any, trackListenerRef.current)
      trackListenerRef.current = null
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'stop_recording' }))
        }
        wsRef.current.close()
      } catch {}
      wsRef.current = null
    }
  }, [room])

  const startWithLang = useCallback(async (selectedLang: SttLang) => {
    if (loading) return
    // Cleanup previous session (but keep lines)
    cleanup()
    setLoading(true)
    setPartial(null)

    try {
      const res = await apiFetch('/api/visio/stt/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: selectedLang }),
      })
      if (!res.ok) {
        console.warn('[subtitles] Failed to create STT session:', await res.text())
        return
      }
      const { ws_url } = await res.json()

      const ws = new WebSocket(ws_url)
      wsRef.current = ws

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve()
        ws.onerror = () => reject(new Error('WebSocket connection failed'))
        setTimeout(() => reject(new Error('WebSocket timeout')), 10000)
      })

      const ctx = new AudioContext({ sampleRate: 16000 })
      await ctx.resume()
      ctxRef.current = ctx
      await ctx.audioWorklet.addModule('/pcm-processor.js')
      const worklet = new AudioWorkletNode(ctx, 'pcm-processor')
      workletRef.current = worklet

      worklet.port.onmessage = (e: MessageEvent) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(e.data as ArrayBuffer)
        }
      }

      const localPub = room?.localParticipant?.getTrackPublications()
      if (localPub) {
        for (const pub of localPub.values()) {
          if (pub.track && pub.track.kind === 'audio' && pub.track.mediaStream) {
            const src = ctx.createMediaStreamSource(pub.track.mediaStream)
            src.connect(worklet)
            sourcesRef.current.push(src)
          }
        }
      }

      const connectRemoteTrack = (track: any) => {
        if (track.kind === 'audio' && track.mediaStream && ctxRef.current && workletRef.current) {
          const src = ctxRef.current.createMediaStreamSource(track.mediaStream)
          src.connect(workletRef.current)
          sourcesRef.current.push(src)
        }
      }

      room?.remoteParticipants?.forEach(p => {
        p.getTrackPublications().forEach(pub => {
          if (pub.track) connectRemoteTrack(pub.track)
        })
      })

      trackListenerRef.current = (_track: any) => { connectRemoteTrack(_track) }
      room?.on('trackSubscribed' as any, trackListenerRef.current)

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string)
          if (msg.type === 'transcript' && msg.data) {
            const { id, is_final, utterance } = msg.data
            if (!utterance?.text) return
            if (!is_final) {
              setPartial({ id, text: utterance.text, isFinal: false, lang: utterance.language || '' })
            } else {
              setPartial(null)
              setLines(prev => {
                const without = prev.filter(l => l.id !== id)
                const updated = [...without, {
                  id,
                  text: utterance.text,
                  isFinal: true,
                  lang: utterance.language || '',
                }]
                if (updated.length > 50) return updated.slice(-50)
                return updated
              })
              // Buffer final lines for backend persistence
              pendingTranscripts.current.push({
                text: utterance.text,
                speaker: room?.localParticipant?.identity || '',
                lang: utterance.language || '',
              })
            }
          }
        } catch {}
      }

      // Flush transcripts to backend every 10s
      flushTimerRef.current = setInterval(flushTranscripts, 10_000)

      ws.onclose = () => {
        setActive(false)
        flushTranscripts()
      }

      slog.action('stt-start', { lang: selectedLang })
      setActive(true)
    } catch (err) {
      slog.error('stt-error', { error: String(err) })
      cleanup()
    } finally {
      setLoading(false)
    }
  }, [loading, room, cleanup])

  const start = useCallback(() => startWithLang(lang), [startWithLang, lang])

  const stop = useCallback(() => {
    slog.action('stt-stop')
    cleanup()
    setActive(false)
    setLines([])
    setPartial(null)
  }, [cleanup])

  const switchLang = useCallback((newLang: SttLang) => {
    setLang(newLang)
    if (active) {
      startWithLang(newLang)
    }
  }, [active, startWithLang])

  return { lines, partial, active, loading, lang, start, stop, switchLang }
}
